import { NotificationSettings } from "@/components/notification-settings";
import {
  useDeleteNotification,
  useGetPushSubscription,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotificationListQuery,
} from "@/lib/queries/notification";
import { Role } from "@repo/core";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowUpDown,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Info,
  Mail,
  MailOpen,
  MoreHorizontal,
  Search,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/(org)/o/$orgSlug/user/notifications")({
  beforeLoad: ({ context }) => {
    if (context.role !== Role.Admin && context.role !== Role.SuperAdmin) {
      throw redirect({ to: "/" });
    }
  },
  component: Notifications,
});

type SortField = "title" | "type" | "createdAt" | "read";
type SortDirection = "asc" | "desc";

function Notifications() {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [readFilter, setReadFilter] = useState<string>("all");
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showReadFilter, setShowReadFilter] = useState(false);
  const limit = 20;

  // Push notification state
  const [pushEndpoint, setPushEndpoint] = useState<string | null>(null);

  // Check for existing push subscription on mount
  useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.ready
        .then((registration) => registration.pushManager.getSubscription())
        .then((subscription) => {
          if (subscription) {
            setPushEndpoint(subscription.endpoint);
          }
        })
        .catch((error) => {
          console.error("Failed to get existing subscription:", error);
        });
    }
  }, []);

  // Fetch notifications
  const { data, isLoading } = useNotificationListQuery(page, limit);

  // Mutations
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const deleteMutation = useDeleteNotification();

  // Push subscription
  const { data: pushSubscription } = useGetPushSubscription(
    pushEndpoint ?? undefined,
  );

  const handleMarkAsRead = (id: string) => {
    markAsReadMutation.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success("All notifications marked as read");
      },
    });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Notification deleted");
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
    });
  };

  const handleBulkDelete = () => {
    const ids = Array.from(selectedIds);
    Promise.all(
      ids.map((id) =>
        deleteMutation.mutateAsync(id).catch(() => {
          /* ignore errors */
        }),
      ),
    ).then(() => {
      toast.success(`Deleted ${ids.length} notification(s)`);
      setSelectedIds(new Set());
    });
  };

  const handleBulkMarkAsRead = () => {
    const ids = Array.from(selectedIds);
    Promise.all(
      ids.map((id) =>
        markAsReadMutation.mutateAsync(id).catch(() => {
          /* ignore errors */
        }),
      ),
    ).then(() => {
      toast.success(`Marked ${ids.length} notification(s) as read`);
      setSelectedIds(new Set());
    });
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCheck className="h-4 w-4 text-green-600" />;
      case "warning":
        return <TriangleAlert className="h-4 w-4 text-yellow-600" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Info className="h-4 w-4 text-blue-600" />;
    }
  };

  const getNotificationBadgeColor = (type: string) => {
    switch (type) {
      case "success":
        return "bg-green-100 text-green-800 border-green-200";
      case "warning":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "error":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  // Filter and sort notifications
  const filteredAndSortedNotifications = useMemo(() => {
    const notifications = data?.items || [];

    // Apply search filter (title + message)
    let filtered = notifications.filter(
      (n) =>
        searchQuery === "" ||
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((n) => n.type === statusFilter);
    }

    // Apply read/unread filter
    if (readFilter === "read") {
      filtered = filtered.filter((n) => n.read);
    } else if (readFilter === "unread") {
      filtered = filtered.filter((n) => !n.read);
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "title":
          comparison = a.title.localeCompare(b.title);
          break;
        case "type":
          comparison = a.type.localeCompare(b.type);
          break;
        case "createdAt":
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "read":
          comparison = (a.read ? 1 : 0) - (b.read ? 1 : 0);
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [
    data?.items,
    searchQuery,
    statusFilter,
    readFilter,
    sortField,
    sortDirection,
  ]);

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAndSortedNotifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAndSortedNotifications.map((n) => n.id)));
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-6xl p-4 md:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold md:text-3xl">Notifications</h1>
          <p className="text-sm text-muted-foreground md:text-base">
            Stay updated with your latest notifications
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  const hasNotifications = filteredAndSortedNotifications.length > 0;
  const allNotifications = data?.items || [];
  const uniqueStatuses = Array.from(
    new Set(allNotifications.map((n) => n.type)),
  );

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl font-bold md:text-3xl">Notifications</h1>
        <p className="text-sm text-muted-foreground md:text-base">
          Stay updated with your latest notifications
        </p>
      </div>

      {/* Settings Section */}
      <div className="mb-4 rounded-lg border bg-card p-3 md:mb-6 md:p-4">
        <NotificationSettings
          pushSubscription={pushSubscription?.endpoint ?? null}
          onSubscriptionChange={setPushEndpoint}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowStatusFilter(!showStatusFilter)}
              className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Status
              <ChevronDown className="h-4 w-4" />
            </button>
            {showStatusFilter && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowStatusFilter(false)}
                />
                <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded-md border bg-popover p-1 shadow-md">
                  <button
                    type="button"
                    onClick={() => {
                      setStatusFilter("all");
                      setShowStatusFilter(false);
                    }}
                    className={`flex w-full items-center rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground ${
                      statusFilter === "all" ? "bg-accent" : ""
                    }`}
                  >
                    All Statuses
                  </button>
                  {uniqueStatuses.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => {
                        setStatusFilter(status);
                        setShowStatusFilter(false);
                      }}
                      className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground ${
                        statusFilter === status ? "bg-accent" : ""
                      }`}
                    >
                      {getNotificationIcon(status)}
                      <span className="capitalize">{status}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Read Filter */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowReadFilter(!showReadFilter)}
              className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Read Status
              <ChevronDown className="h-4 w-4" />
            </button>
            {showReadFilter && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowReadFilter(false)}
                />
                <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded-md border bg-popover p-1 shadow-md">
                  <button
                    type="button"
                    onClick={() => {
                      setReadFilter("all");
                      setShowReadFilter(false);
                    }}
                    className={`flex w-full items-center rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground ${
                      readFilter === "all" ? "bg-accent" : ""
                    }`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReadFilter("unread");
                      setShowReadFilter(false);
                    }}
                    className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground ${
                      readFilter === "unread" ? "bg-accent" : ""
                    }`}
                  >
                    <Mail className="h-4 w-4" />
                    Unread
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReadFilter("read");
                      setShowReadFilter(false);
                    }}
                    className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground ${
                      readFilter === "read" ? "bg-accent" : ""
                    }`}
                  >
                    <MailOpen className="h-4 w-4" />
                    Read
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Bulk Actions */}
          {selectedIds.size > 0 && (
            <>
              <button
                type="button"
                onClick={handleBulkMarkAsRead}
                className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <CheckCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Mark Read</span>
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-destructive hover:text-destructive-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Trash2 className="h-4 w-4" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </>
          )}

          {/* Mark All as Read */}
          {allNotifications.length > 0 && (
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isPending}
              className="flex h-9 items-center gap-2 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              {markAllAsReadMutation.isPending ? "Marking..." : "Mark All Read"}
            </button>
          )}
        </div>
      </div>

      {/* Empty State */}
      {!hasNotifications ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 py-16">
          <Info className="mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold text-foreground">
            No notifications
          </h3>
          <p className="text-sm text-muted-foreground">
            {searchQuery || statusFilter !== "all" || readFilter !== "all"
              ? "No notifications match your filters"
              : "You're all caught up! No new notifications."}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View - Hidden on Mobile */}
          <div className="hidden rounded-md border md:block">
            {/* Table Header */}
            <div className="flex items-center border-b bg-muted/50 px-4 py-3 text-sm font-medium text-muted-foreground">
              <div className="flex w-10 items-center justify-center">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.size ===
                      filteredAndSortedNotifications.length &&
                    filteredAndSortedNotifications.length > 0
                  }
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
              </div>
              <button
                type="button"
                onClick={() => toggleSort("read")}
                className="flex w-16 items-center justify-center gap-1 transition-colors hover:text-foreground"
              >
                Read
                {sortField === "read" && <ArrowUpDown className="h-3 w-3" />}
              </button>
              <button
                type="button"
                onClick={() => toggleSort("title")}
                className="flex flex-1 items-center gap-1 transition-colors hover:text-foreground"
              >
                Title
                {sortField === "title" && <ArrowUpDown className="h-3 w-3" />}
              </button>
              <button
                type="button"
                onClick={() => toggleSort("type")}
                className="flex w-32 items-center justify-center gap-1 transition-colors hover:text-foreground"
              >
                Status
                {sortField === "type" && <ArrowUpDown className="h-3 w-3" />}
              </button>
              <button
                type="button"
                onClick={() => toggleSort("createdAt")}
                className="flex w-40 items-center justify-end gap-1 transition-colors hover:text-foreground"
              >
                Date
                {sortField === "createdAt" && (
                  <ArrowUpDown className="h-3 w-3" />
                )}
              </button>
              <div className="w-10" />
            </div>

            {/* Table Body */}
            <div className="divide-y">
              {filteredAndSortedNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`group flex items-center px-4 py-3 text-sm transition-colors hover:bg-muted/50 ${
                    notification.read ? "opacity-60" : ""
                  }`}
                >
                  {/* Checkbox */}
                  <div className="flex w-10 items-center justify-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(notification.id)}
                      onChange={() => toggleSelection(notification.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    />
                  </div>

                  {/* Read/Unread Icon - Clickable */}
                  <div className="flex w-16 items-center justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (notification.read) {
                          // Mark as unread - we'll need to add this mutation
                          toast.info("Mark as unread not yet implemented");
                        } else {
                          handleMarkAsRead(notification.id);
                        }
                      }}
                      className="rounded p-1 transition-colors hover:bg-accent"
                      title={
                        notification.read ? "Mark as unread" : "Mark as read"
                      }
                    >
                      {notification.read ? (
                        <MailOpen className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Mail className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  </div>

                  {/* Title & Message */}
                  <div className="flex flex-1 items-center gap-3">
                    {getNotificationIcon(notification.type)}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-foreground">
                        {notification.title}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {notification.message}
                      </p>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex w-32 justify-center">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getNotificationBadgeColor(notification.type)}`}
                    >
                      {notification.type}
                    </span>
                  </div>

                  {/* Date */}
                  <div className="w-40 text-right text-xs text-muted-foreground">
                    {new Date(notification.createdAt).toLocaleDateString(
                      undefined,
                      {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </div>

                  {/* Actions Menu */}
                  <div className="relative w-10">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenMenuId(
                          openMenuId === notification.id
                            ? null
                            : notification.id,
                        )
                      }
                      className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>

                    {/* Dropdown Menu */}
                    {openMenuId === notification.id && (
                      <>
                        {/* Backdrop */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenMenuId(null)}
                        />
                        {/* Menu */}
                        <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded-md border bg-popover p-1 shadow-md">
                          {!notification.read && (
                            <button
                              type="button"
                              onClick={() => {
                                handleMarkAsRead(notification.id);
                                setOpenMenuId(null);
                              }}
                              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-popover-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              <CheckCheck className="h-4 w-4" />
                              Mark as read
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              handleDelete(notification.id);
                              setOpenMenuId(null);
                            }}
                            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Card View - Visible only on Mobile */}
          <div className="space-y-3 md:hidden">
            {filteredAndSortedNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`rounded-lg border bg-card p-4 transition-colors ${
                  notification.read ? "opacity-60" : ""
                } ${selectedIds.has(notification.id) ? "ring-2 ring-primary" : ""}`}
              >
                {/* Header Row */}
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedIds.has(notification.id)}
                      onChange={() => toggleSelection(notification.id)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    />

                    {/* Icon & Title */}
                    <div className="flex-1 min-w-0">
                      <div className="mb-1 flex items-center gap-2">
                        {getNotificationIcon(notification.type)}
                        <h3 className="font-medium text-foreground">
                          {notification.title}
                        </h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {notification.message}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="relative flex shrink-0 items-center gap-2">
                    {/* Read/Unread Toggle */}
                    <button
                      type="button"
                      onClick={() => {
                        if (notification.read) {
                          toast.info("Mark as unread not yet implemented");
                        } else {
                          handleMarkAsRead(notification.id);
                        }
                      }}
                      className="rounded p-1.5 transition-colors hover:bg-accent"
                      title={
                        notification.read ? "Mark as unread" : "Mark as read"
                      }
                    >
                      {notification.read ? (
                        <MailOpen className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Mail className="h-4 w-4 text-primary" />
                      )}
                    </button>

                    {/* More Menu */}
                    <button
                      type="button"
                      onClick={() =>
                        setOpenMenuId(
                          openMenuId === notification.id
                            ? null
                            : notification.id,
                        )
                      }
                      className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>

                    {/* Dropdown Menu */}
                    {openMenuId === notification.id && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenMenuId(null)}
                        />
                        <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded-md border bg-popover p-1 shadow-md">
                          {!notification.read && (
                            <button
                              type="button"
                              onClick={() => {
                                handleMarkAsRead(notification.id);
                                setOpenMenuId(null);
                              }}
                              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-popover-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              <CheckCheck className="h-4 w-4" />
                              Mark as read
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              handleDelete(notification.id);
                              setOpenMenuId(null);
                            }}
                            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer Row */}
                <div className="flex items-center justify-between gap-3 border-t pt-3">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getNotificationBadgeColor(notification.type)}`}
                  >
                    {notification.type}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(notification.createdAt).toLocaleDateString(
                      undefined,
                      {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {hasNotifications && (
        <div className="mt-4 flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>
            {selectedIds.size > 0
              ? `${selectedIds.size} of ${filteredAndSortedNotifications.length} row(s) selected`
              : `Showing ${filteredAndSortedNotifications.length} of ${allNotifications.length} notification(s)`}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!data?.hasPreviousPage}
              className="flex h-8 flex-1 items-center justify-center gap-1 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:flex-initial"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!data?.hasNextPage}
              className="flex h-8 flex-1 items-center justify-center gap-1 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:flex-initial"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
